import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import { type SelectListProps, SelectOperation } from '@openx/types/selectableTable';
import type { BaseRow, Columns } from '@openx/types/tableHelpers';
import { STICKY_POSITION } from '@openx/types/tableHelpers';
import { isInArray } from '@openx/utils/lib/isInArray';
import isEqual from 'lodash/isEqual';
import type { ReactNode } from 'react';

import { allItemsSelected, anyItemSelected } from './selectCheckers';

export function extendColumnsWithCheckbox<RowT extends BaseRow>(
  columns: Columns<RowT>,
  allItems: RowT[],
  selectList: SelectListProps<RowT>,
  disabled = false
): Columns<RowT> {
  const { selectedItems, toggleSelect, onToggleAll, disabledItems } = selectList;

  function isSelected(item: RowT): boolean {
    return isInArray(selectedItems, item, isEqual);
  }

  function isDisabled(item: RowT): boolean {
    if (!disabledItems) return false;

    return isInArray(disabledItems, item, isEqual);
  }

  const selectAllCheckbox: ReactNode = (
    <Checkbox
      checked={anyItemSelected(allItems, selectedItems)}
      indeterminate={anyItemSelected(allItems, selectedItems) && !allItemsSelected(allItems, selectedItems)}
      onClick={e => e.stopPropagation()}
      onChange={event => {
        const { checked } = event.target;
        onToggleAll
          ? onToggleAll()
          : toggleSelect(allItems, checked ? SelectOperation.SELECT : SelectOperation.UNSELECT);
      }}
      disabled={disabled}
      color="primary"
      key="select-all"
    />
  );

  function singleCheckboxRenderer(item: RowT): ReactNode {
    return (
      <Checkbox
        checked={isSelected(item)}
        disabled={isDisabled(item)}
        onClick={e => e.stopPropagation()}
        onChange={event => {
          const { checked } = event.target;
          toggleSelect([item], checked ? SelectOperation.SELECT : SelectOperation.UNSELECT);
        }}
        color="primary"
      />
    );
  }

  return [
    {
      key: 'select',
      render: singleCheckboxRenderer,
      title: selectAllCheckbox,
      width: '48px',
      align: 'center',
      dataTest: 'table-cell-select',
      sticky: columns[0]?.sticky === STICKY_POSITION.LEFT ? STICKY_POSITION.LEFT : undefined,
    },
    ...columns,
  ];
}
